import styles from "../../ConversionAndPdpViewer.module.css";
import {
  formatCurrency,
  formatCurrencyPrecise,
  formatDelta,
  formatFractionPct,
  formatNumber,
  formatPct,
  resolveProductUrl,
} from "./formatters";
import { MetricCard } from "./ui";

export default function PdpDeepDiveTab({
  selectedProduct,
  currency = "USD",
  onAnalyze = null,
  isAnalyzing = false,
  isCheckingAnalysis = false,
  analysisError = "",
  hasAnalysis = false,
  seoData = null,
  gscData = null,
}) {
  if (!selectedProduct) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateTitle}>
          Select a product from PDP Rankings to analyze
        </div>
        <div className={styles.emptyStateText}>
          Click any product row in the rankings tab to open its deep dive analysis.
        </div>
      </div>
    );
  }

  const competitorRows = selectedProduct?.competitorPricing?.competitors || [];
  const wf = selectedProduct?.waterfall || {};
  const cq = selectedProduct.conversionQuality || {};
  const co = selectedProduct.commercialOutcome || {};
  const sb = selectedProduct.supportingBehavior || {};
  const interp = selectedProduct.interpretation || {};
  const productPageUrl = resolveProductUrl(selectedProduct.url);
  const purchaseCount =
    co.purchaseCount ??
    co.unitsSold ??
    wf.purchase ??
    wf.checkedOut ??
    0;
  const viewCount =
    wf.views ??
    wf.pdpViews ??
    selectedProduct.itemViewEvents ??
    0;
  const checkoutCount =
    wf.checkedOut ??
    wf.checkout ??
    selectedProduct.itemsCheckedOut ??
    purchaseCount ??
    0;
  const addToCartCount =
    wf.addedToCart ??
    wf.addToCart ??
    0;
  const pdpToAtcRate =
    addToCartCount > 0 || viewCount > 0
      ? viewCount > 0
        ? addToCartCount / viewCount
        : null
      : null;
  const pdpToPurchaseRate =
    purchaseCount > 0 || viewCount > 0
      ? viewCount > 0
        ? purchaseCount / viewCount
        : null
      : null;
  const atcToPurchaseRate =
    cq.atcToPurchaseRate != null
      ? cq.atcToPurchaseRate
      : addToCartCount > 0
        ? purchaseCount / addToCartCount
        : null;
  const contributionMissing =
    co.contributionStatus === "missing_cost_data" ||
    co.contribution == null;

  const displayPrice = co.unitPrice != null
    ? co.unitPrice
    : (() => {
        const rev = co.revenue ?? selectedProduct?.itemRevenue ?? 0;
        const units = co.unitsSold ?? selectedProduct?.unitsSold ?? 0;
        return units > 0 && rev > 0 ? Math.round((rev / units) * 100) / 100 : null;
      })();

  return (
    <div className={styles.sectionStack}>
      <div className={styles.pdpHeader}>
        <div>
          <div className={styles.pdpName}>{selectedProduct.title || selectedProduct.itemName || "—"}</div>
          {selectedProduct.category ? (
            <div className={styles.pdpCategory}>{selectedProduct.category}</div>
          ) : null}
          <div className={styles.pdpActionRow}>
            {productPageUrl ? (
              <button
                type="button"
                className={styles.pdpOpenButton}
                onClick={() => window.open(productPageUrl, "_blank", "noopener,noreferrer")}
              >
                Open product page
              </button>
            ) : null}
            {typeof onAnalyze === "function" ? (
              <button
                type="button"
                className={styles.pdpAnalyzeButton}
                onClick={onAnalyze}
                disabled={isAnalyzing || isCheckingAnalysis}
              >
                {isAnalyzing
                  ? "Analyzing…"
                  : isCheckingAnalysis
                    ? "Checking…"
                    : hasAnalysis
                      ? "Re-analyze"
                      : "Analyze"}
              </button>
            ) : null}
          </div>
          {analysisError ? (
            <div className={styles.pdpAnalysisError}>{analysisError}</div>
          ) : null}
        </div>

        <div className={styles.pdpPriceBox}>
          <div className={styles.pdpPrice}>
            {displayPrice != null ? formatCurrencyPrecise(displayPrice, currency) : "—"}
          </div>
          <div className={styles.pdpMargin}>
            {!contributionMissing && co.marginPct != null ? `${formatPct(co.marginPct * 100)} margin` : "margin N/A"}
          </div>
        </div>
      </div>

      <div className={styles.sectionHeading}>PDP waterfall</div>
      <div className={styles.metricGrid4}>
        <MetricCard label="PDP views" value={formatNumber(viewCount)} />
        <MetricCard label="Add to cart" value={formatNumber(addToCartCount)} />
        <MetricCard label="Checkout" value={formatNumber(checkoutCount)} />
        <MetricCard label="Purchase" value={formatNumber(purchaseCount)} />
      </div>


      <div className={styles.sectionHeading}>Conversion quality</div>
      <div className={styles.metricGrid3}>
        <MetricCard label="PDP to ATC" value={formatFractionPct(pdpToAtcRate ?? cq.atcRate ?? cq.pdpToAtcRate ?? cq.cartToViewRate)} />
        <MetricCard label="PDP to purchase" value={formatFractionPct(pdpToPurchaseRate ?? cq.purchaseToViewRate ?? cq.pdpToPurchaseRate)} />
        <MetricCard label="ATC to purchase" value={formatFractionPct(atcToPurchaseRate)} />
      </div>

      {cq.bundleBehavior?.detected && cq.bundleBehavior?.note ? (
        <div className={styles.bundleNote}>
          <div className={styles.bundleNoteLabel}>Bundle / cross-sell purchase detected</div>
          {cq.bundleBehavior.note}
        </div>
      ) : null}

      <div className={styles.sectionHeading}>Commercial outcome</div>
      <div className={styles.metricGrid4}>
        <MetricCard label="Revenue" value={formatCurrency(co.revenue, currency)} />
        <MetricCard
          label={<span title="contribution before marketing">Contribution</span>}
          value={co.contribution != null ? formatCurrency(co.contribution, currency) : "—"}
        />
        <MetricCard label="Rev/view" value={formatCurrencyPrecise(co.revenuePerView ?? co.revenuePerSession, currency)} />
        <MetricCard label="Contrib/view" value={!contributionMissing && co.contributionPerView != null ? formatCurrencyPrecise(co.contributionPerView, currency) : !contributionMissing && co.contributionPerSession != null ? formatCurrencyPrecise(co.contributionPerSession, currency) : "—"} />
      </div>

      {hasAnalysis && (() => {
        const cp = selectedProduct?.competitorPricing;
        const ma = cp?.marginAnalysis;
        const pricePositionLabel = {
          below_market: "Below market",
          at_market: "At market",
          above_market: "Above market",
          premium: "Premium",
          unknown: "Unknown",
        }[cp?.pricePosition] || cp?.pricePosition || "—";

        return (
          <>
            <div className={styles.sectionHeading}>Competitor pricing</div>
            {competitorRows.length ? (
              <>
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Competitor</th>
                        <th className={styles.alignRight}>Price</th>
                        <th className={styles.alignRight}>Product page</th>
                      </tr>
                    </thead>
                    <tbody>
                      {competitorRows.map((row, index) => (
                        <tr key={`${row.name}-${index}`}>
                          <td>{row.name}</td>
                          <td className={styles.alignRight}>
                            {typeof row.price === "number" ? formatCurrencyPrecise(row.price, currency) : <span style={{ color: "var(--tm)" }}>—</span>}
                          </td>
                          <td className={styles.alignRight} style={{ fontSize: "0.78rem" }}>
                            {row.productUrl ? (
                              <a
                                href={row.productUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "var(--accent, #2563eb)", textDecoration: "underline" }}
                              >
                                {row.source || "View"}
                              </a>
                            ) : (
                              <span style={{ color: "var(--tm)" }}>{row.source || "—"}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className={styles.metricGrid2}>
                  <MetricCard
                    label="Competitor avg"
                    value={cp?.competitorAveragePrice != null ? formatCurrencyPrecise(cp.competitorAveragePrice, currency) : "—"}
                  />
                  <MetricCard
                    label="Price position"
                    value={pricePositionLabel}
                    tone={
                      cp?.pricePosition === "below_market" ? "positive" :
                      cp?.pricePosition === "premium" || cp?.pricePosition === "above_market" ? "negative" : "neutral"
                    }
                  />
                </div>
                {cp?.pricePositionSummary && (
                  <div className={styles.bundleNote}>
                    <div className={styles.bundleNoteLabel}>Market positioning</div>
                    {cp.pricePositionSummary}
                  </div>
                )}
                {ma?.canAnalyze && (
                  <div className={styles.interpretationCard}>
                    <div className={styles.interpretationSection}>
                      <div className={styles.interpretationLabel}>Can we match or go below market?</div>
                      <div className={styles.interpretationText}>
                        <span className={ma.canMatchMarket ? styles.positive : styles.negative}>
                          {ma.canMatchMarket ? "Yes — room to discount" : "No — cost floor reached"}
                        </span>
                      </div>
                    </div>
                    {ma.canMatchMarket ? (
                      <>
                        <div className={styles.interpretationSection}>
                          <div className={styles.interpretationLabel}>Max discount</div>
                          <div className={styles.interpretationText}>{ma.maxDiscountPct}% (down to {formatCurrencyPrecise(ma.competitorAveragePrice, currency)} at {Math.round((ma.marginAtCompetitorPrice || 0) * 100)}% margin)</div>
                        </div>
                      </>
                    ) : (
                      <div className={styles.interpretationSection}>
                        <div className={styles.interpretationLabel}>Minimum viable price</div>
                        <div className={styles.interpretationText}>{formatCurrencyPrecise(ma.minimumViablePrice, currency)} (to maintain 20% margin)</div>
                      </div>
                    )}
                    <div className={styles.interpretationSection}>
                      <div className={styles.interpretationLabel}>Pricing recommendation</div>
                      <div className={styles.interpretationText}>{ma.recommendation}</div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.bundleNote} style={{ color: "var(--tm)" }}>
                No competitor prices found for this product. Re-analyze to try again.
              </div>
            )}
          </>
        );
      })()}

      {/*
      {hasTrends ? (
        <>
          <div className={styles.sectionHeading}>Trends</div>
          <div className={styles.metricGrid4}>
            <MetricCard
              label="Traffic trend"
              value={trends.trafficChangePct != null ? formatDelta(trends.trafficChangePct) : "—"}
              tone={trends.trafficChangePct != null ? (Number(trends.trafficChangePct) >= 0 ? "positive" : "negative") : "neutral"}
            />
            <MetricCard
              label="ATC trend"
              value={trends.atcRateChangePp != null ? formatDelta(trends.atcRateChangePp, 1, "pp") : "—"}
              tone={trends.atcRateChangePp != null ? (Number(trends.atcRateChangePp) >= 0 ? "positive" : "negative") : "neutral"}
            />
            <MetricCard
              label="Purchase trend"
              value={trends.purchaseRateChangePp != null ? formatDelta(trends.purchaseRateChangePp, 1, "pp") : "—"}
              tone={trends.purchaseRateChangePp != null ? (Number(trends.purchaseRateChangePp) >= 0 ? "positive" : "negative") : "neutral"}
            />
            <MetricCard
              label="Search trend"
              value={trends.searchTrendChangePct != null ? formatDelta(trends.searchTrendChangePct) : "—"}
            />
          </div>
        </>
      ) : null}
      */}

      {(sb.avgTimeOnPdpSeconds != null || sb.exitRate != null || sb.bounceRate != null || gscData || selectedProduct?.gscData) && (() => {
        const gsc = gscData || selectedProduct?.gscData || null;
        return (
          <>
            <div className={styles.sectionHeading}>Supporting behavior</div>
            <div className={styles.metricGrid4}>
              <MetricCard
                label="Avg time on PDP"
                value={
                  sb.avgTimeOnPdpDisplay ||
                  (sb.avgTimeOnPdpSeconds != null
                    ? `${Math.round(Number(sb.avgTimeOnPdpSeconds))}s`
                    : "—")
                }
              />
              <MetricCard
                label="Exit rate"
                value={
                  sb.exitRate != null
                    ? formatPct(sb.exitRate)
                    : sb.bounceRate != null
                      ? formatPct(sb.bounceRate)
                      : "—"
                }
              />
              <MetricCard
                label="Impressions"
                value={gsc?.impressions != null ? formatNumber(gsc.impressions) : "—"}
              />
              <MetricCard
                label="CTR"
                value={gsc?.ctr_pct != null ? `${Number(gsc.ctr_pct).toFixed(1)}%` : "—"}
              />
            </div>
          </>
        );
      })()}

      {seoData?.recommendedActions?.length > 0 && (
        <>
          <div className={styles.sectionHeading}>Recommended actions</div>
          <div className={styles.interpretationCard}>
            {seoData.recommendedActions.map((action, i) => (
              <div key={i} className={styles.interpretationSection} style={{ flexDirection: "row", gap: 10, alignItems: "flex-start" }}>
                <span style={{ flexShrink: 0, fontWeight: 700, color: "var(--accent, #2563eb)", fontSize: "0.85rem" }}>{i + 1}.</span>
                <div className={styles.interpretationText}>{action}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {seoData && (
        <>
          <div className={styles.sectionHeading}>SEO & content audit</div>
          <div className={styles.interpretationCard}>
            <div className={styles.interpretationSection}>
              <div className={styles.interpretationLabel}>Meta title</div>
              {seoData.metaTitle ? (
                <div className={styles.interpretationText}>{seoData.metaTitle}</div>
              ) : (
                <div className={styles.interpretationText} style={{ color: "var(--tm)" }}>Not set</div>
              )}
              {seoData.recommendedMetaTitle && (
                <div style={{ marginTop: 6, padding: "6px 10px", background: "var(--surface-subtle, rgba(0,0,0,0.04))", borderRadius: 6 }}>
                  <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--tm)", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>Recommended</div>
                  <div className={styles.interpretationText}>{seoData.recommendedMetaTitle}</div>
                </div>
              )}
            </div>
            <div className={styles.interpretationSection}>
              <div className={styles.interpretationLabel}>Meta description</div>
              {seoData.metaDescription ? (
                <div className={styles.interpretationText}>{seoData.metaDescription}</div>
              ) : (
                <div className={styles.interpretationText} style={{ color: "var(--tm)" }}>Not set</div>
              )}
              {seoData.recommendedMetaDescription && (
                <div style={{ marginTop: 6, padding: "6px 10px", background: "var(--surface-subtle, rgba(0,0,0,0.04))", borderRadius: 6 }}>
                  <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--tm)", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>Recommended</div>
                  <div className={styles.interpretationText}>{seoData.recommendedMetaDescription}</div>
                </div>
              )}
            </div>
            <div className={styles.interpretationSection}>
              <div className={styles.interpretationLabel}>
                Image alt texts {seoData.images?.length ? `(${seoData.images.length})` : ""}
              </div>
              {seoData.images?.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
                  {seoData.images.map((img, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      {img.url && (
                        <img
                          src={img.url}
                          alt={img.altText || ""}
                          style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 6, flexShrink: 0, border: "1px solid var(--border-subtle)" }}
                        />
                      )}
                      <div className={styles.interpretationText} style={{ marginTop: img.url ? 4 : 0 }}>
                        {img.altText || <span style={{ color: "var(--tm)" }}>No alt text</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.interpretationText} style={{ color: "var(--tm)" }}>None set</div>
              )}
            </div>
          </div>
        </>
      )}

      {(interp.overall || interp.standout || interp.meaning || interp.nextStep) && (
        <div className={styles.interpretationCard}>
          {interp.overall && (
            <div className={styles.interpretationSection}>
              <div className={styles.interpretationLabel}>Overall reading</div>
              <div className={styles.interpretationText}>{interp.overall}</div>
            </div>
          )}
          {interp.standout && (
            <div className={styles.interpretationSection}>
              <div className={styles.interpretationLabel}>What stands out</div>
              <div className={styles.interpretationText}>{interp.standout}</div>
            </div>
          )}
          {interp.meaning && (
            <div className={styles.interpretationSection}>
              <div className={styles.interpretationLabel}>What this likely means</div>
              <div className={styles.interpretationText}>{interp.meaning}</div>
            </div>
          )}
          {interp.nextStep && (
            <div className={styles.interpretationSection}>
              <div className={styles.interpretationLabel}>Recommended next step</div>
              <div className={styles.interpretationText}>{interp.nextStep}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
